import { useEffect } from 'react';
import './DracoMeteorAnimation.css';

/**
 * PROFESSIONAL DRACO METEOR ANIMATION
 * Dragon-type special move with authentic Pokémon-style effects
 * 
 * Key Features:
 * - High-power special move (130 base power)
 * - Comets summoned from sky crashing down on target
 * - User Special Attack reduction recoil effect
 * - Professional gaming company quality with dramatic timing
 * - Dragon-type themed effects with purple/blue energy
 */
export default function DracoMeteorAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = (playerRect.left + playerRect.width / 2) - containerRect.left;
    const playerY = (playerRect.top + playerRect.height / 2) - containerRect.top;
    const opponentX = (opponentRect.left + opponentRect.width / 2) - containerRect.left;
    const opponentY = (opponentRect.top + opponentRect.height / 2) - containerRect.top;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Dragon Energy Buildup (0-500ms)
    console.log('Draco Meteor Phase 1: Dragon Energy Buildup');
    
    // Player enhancement
    playerEl.classList.add('draco-meteor-user-charge');
    
    // Dragon aura around player
    const dragonAura = document.createElement('div');
    dragonAura.className = 'draco-meteor-dragon-aura';
    dragonAura.style.left = `${playerX - 60}px`;
    dragonAura.style.top = `${playerY - 60}px`;
    container.appendChild(dragonAura);
    elements.push(dragonAura);

    // Energy particles around player
    for (let i = 0; i < 16; i++) {
      const particle = document.createElement('div');
      particle.className = 'draco-meteor-energy-particle';
      const angle = (i / 16) * 360;
      const radius = 80;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      particle.style.left = `${particleX - 4}px`;
      particle.style.top = `${particleY - 4}px`;
      particle.style.animationDelay = `${i * 30}ms`;
      container.appendChild(particle);
      elements.push(particle);
    }

    // PHASE 2: Meteor Summoning (500-1200ms)
    setTimeout(() => {
      console.log('Draco Meteor Phase 2: Meteor Summoning');
      
      // Screen tint for dramatic effect
      const screenTint = document.createElement('div');
      screenTint.className = 'draco-meteor-screen-tint';
      container.appendChild(screenTint);
      elements.push(screenTint);

      // Create 5 meteors with enhanced visuals but original vertical trajectory
      const meteorData = [
        { size: 1.2, delay: 0, startOffset: -80 },
        { size: 0.8, delay: 150, startOffset: -40 },
        { size: 1.0, delay: 80, startOffset: 0 },
        { size: 0.9, delay: 200, startOffset: 40 },
        { size: 1.1, delay: 120, startOffset: 80 }
      ];

      meteorData.forEach((data, i) => {
        const meteor = document.createElement('div');
        meteor.className = 'draco-meteor-comet';
        
        // Original vertical trajectory - straight down
        const startX = opponentX + data.startOffset;
        const startY = opponentY - 350; // High above opponent
        
        meteor.style.left = `${startX - 15}px`;
        meteor.style.top = `${startY}px`;
        meteor.style.transform = `scale(${data.size})`;
        meteor.style.animationDelay = `${data.delay}ms`;
        container.appendChild(meteor);
        elements.push(meteor);

        // Enhanced meteor trail - vertical
        const trail = document.createElement('div');
        trail.className = 'draco-meteor-trail';
        trail.style.left = `${startX - 3}px`;
        trail.style.top = `${startY}px`;
        trail.style.transform = `scale(${data.size})`;
        trail.style.animationDelay = `${data.delay}ms`;
        container.appendChild(trail);
        elements.push(trail);

        // Add meteor glow effect - vertical
        const glow = document.createElement('div');
        glow.className = 'draco-meteor-glow';
        glow.style.left = `${startX - 25}px`;
        glow.style.top = `${startY - 10}px`;
        glow.style.transform = `scale(${data.size})`;
        glow.style.animationDelay = `${data.delay}ms`;
        container.appendChild(glow);
        elements.push(glow);
      });
    }, 500);

    // PHASE 3: Meteor Impact (1200-2000ms)
    setTimeout(() => {
      console.log('Draco Meteor Phase 3: Meteor Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('draco-meteor-opponent-hit');

      // Define meteor data for impacts (same as summoning phase)
      const meteorData = [
        { size: 1.2, delay: 0, startOffset: -80 },
        { size: 0.8, delay: 150, startOffset: -40 },
        { size: 1.0, delay: 80, startOffset: 0 },
        { size: 0.9, delay: 200, startOffset: 40 },
        { size: 1.1, delay: 120, startOffset: 80 }
      ];

      // Create impact explosions
      meteorData.forEach((data, i) => {
        setTimeout(() => {
          const impactX = opponentX + data.startOffset;
          const impactY = opponentY;
          
          // Crater impact effect
          const crater = document.createElement('div');
          crater.className = 'draco-meteor-crater';
          crater.style.left = `${impactX - 30}px`;
          crater.style.top = `${impactY - 15}px`;
          crater.style.transform = `scale(${data.size})`;
          container.appendChild(crater);
          elements.push(crater);
          
          // Enhanced explosion with size variation
          const explosion = document.createElement('div');
          explosion.className = 'draco-meteor-explosion';
          explosion.style.left = `${impactX - 50}px`;
          explosion.style.top = `${impactY - 50}px`;
          explosion.style.transform = `scale(${data.size})`;
          container.appendChild(explosion);
          elements.push(explosion);

          // Impact shockwave with size variation
          const shockwave = document.createElement('div');
          shockwave.className = 'draco-meteor-shockwave';
          shockwave.style.left = `${impactX - 40}px`;
          shockwave.style.top = `${impactY - 40}px`;
          shockwave.style.transform = `scale(${data.size})`;
          container.appendChild(shockwave);
          elements.push(shockwave);

          // Enhanced debris with realistic physics
          const debrisCount = Math.floor(8 * data.size);
          for (let j = 0; j < debrisCount; j++) {
            const debris = document.createElement('div');
            debris.className = 'draco-meteor-debris';
            const debrisAngle = (j / debrisCount) * 360 + (Math.random() - 0.5) * 60;
            const debrisRadius = 40 + Math.random() * 40;
            const debrisX = impactX + Math.cos(debrisAngle * Math.PI / 180) * debrisRadius;
            const debrisY = impactY + Math.sin(debrisAngle * Math.PI / 180) * debrisRadius;
            debris.style.left = `${debrisX - 3}px`;
            debris.style.top = `${debrisY - 3}px`;
            debris.style.transform = `scale(${0.5 + Math.random() * 0.5})`;
            debris.style.animationDelay = `${j * 30}ms`;
            container.appendChild(debris);
            elements.push(debris);
          }
        }, data.delay + 500); // Use meteor-specific delay
      });
    }, 1200);

    // PHASE 4: User Recoil Effect (2000-2400ms)
    setTimeout(() => {
      console.log('Draco Meteor Phase 4: User Recoil Effect');
      
      // Remove charge effect
      playerEl.classList.remove('draco-meteor-user-charge');
      
      // Add recoil effect showing Special Attack reduction
      playerEl.classList.add('draco-meteor-user-recoil');

      // Recoil indicator removed - visual effects only

      // Recoil energy drain particles
      for (let i = 0; i < 12; i++) {
        const drainParticle = document.createElement('div');
        drainParticle.className = 'draco-meteor-drain-particle';
        const angle = (i / 12) * 360;
        const radius = 50;
        const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
        const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
        drainParticle.style.left = `${particleX - 3}px`;
        drainParticle.style.top = `${particleY - 3}px`;
        drainParticle.style.animationDelay = `${i * 40}ms`;
        container.appendChild(drainParticle);
        elements.push(drainParticle);
      }
    }, 2000);

    // CLEANUP (2800ms - extended to ensure all impact elements are created first)
    const cleanupTimer = setTimeout(() => {
      console.log('Draco Meteor: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('draco-meteor-user-charge', 'draco-meteor-user-recoil');
      opponentEl.classList.remove('draco-meteor-opponent-hit');
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Also remove any elements with draco-meteor classes that might have been missed
      const dracoElements = container.querySelectorAll('[class*="draco-meteor"]');
      dracoElements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 2800);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('draco-meteor-user-charge', 'draco-meteor-user-recoil');
      opponentEl.classList.remove('draco-meteor-opponent-hit');
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      // Fallback cleanup for any missed elements
      const dracoElements = container.querySelectorAll('[class*="draco-meteor"]');
      dracoElements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
DracoMeteorAnimation.moveType = 'dragon';
